// ===========================================
// COMPONENTE: EventoDetalleDialog
// ===========================================
// Dialog for viewing/editing an existing calendar event

import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { CalendarDays, Pencil, Trash2, Loader2, MapPin, Clock, Video, RefreshCw, AlertTriangle, Repeat } from 'lucide-react'
import { format } from 'date-fns'
import { es } from 'date-fns/locale'
import { parseFechaEvento } from '@/lib/fechaEvento'
import {
  TIPO_EVENTO_CONFIG,
  ESTADO_EVENTO_CONFIG,
  COLORES_CALENDARIO,
  type EventoCalendario,
  type EventoCalendarioUpdate,
  type TipoEvento,
  type EstadoEvento,
} from '@/core/types/calendario'
import {
  useCrearEventoForm,
  VALORES_INICIALES,
  getFieldError,
  type CrearEventoFormValues,
} from './forms'
import { useReintentarReunionGoogle } from '@/core/hooks/useCalendario'
import { InvitadosReunion } from './InvitadosReunion'
import { describirRecurrencia } from '@/core/lib/recurrencia-eventos'
import { useStore } from '@tanstack/react-form'
import { FormDialogContent } from '@/components/ui/form-dialog-content'

interface EventoDetalleDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  evento: EventoCalendario | null
  onActualizar: (id: string, datos: EventoCalendarioUpdate) => Promise<void>
  onEliminar: (id: string) => Promise<void>
}

// Exclude 'vencimiento' from selectable types
const TIPOS_SELECCIONABLES = (
  Object.entries(TIPO_EVENTO_CONFIG) as [TipoEvento, { label: string; color: string }][]
).filter(([key]) => key !== 'vencimiento')

export function EventoDetalleDialog({
  open,
  onOpenChange,
  evento,
  onActualizar,
  onEliminar,
}: EventoDetalleDialogProps) {
  const [editando, setEditando] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  // Parse datetime into date + time parts
  const parseFechaHora = (fechaISO: string | null) => {
    if (!fechaISO) return { fecha: '', hora: '' }
    try {
      // `parseFechaEvento`, no `new Date`: si no, al abrir el form la fecha ya venía corrida
      // un día y el submit la reescribía, así que cada edición movía el evento hacia atrás.
      const d = parseFechaEvento(fechaISO)
      return {
        fecha: format(d, 'yyyy-MM-dd'),
        hora: format(d, 'HH:mm'),
      }
    } catch (err) {
      console.warn('[EventoDetalleDialog] fecha inválida:', fechaISO, err)
      return { fecha: '', hora: '' }
    }
  }

  const inicioInfo = parseFechaHora(evento?.fecha_inicio ?? null)
  const finInfo = parseFechaHora(evento?.fecha_fin ?? null)

  // Lo que se MUESTRA es la ocurrencia elegida; lo que se EDITA, el maestro (ORR-383).
  const inicioVisible = evento?.ocurrencia_inicio ?? evento?.fecha_inicio ?? null
  const finVisible =
    evento?.ocurrencia_inicio !== undefined ? evento.ocurrencia_fin ?? null : evento?.fecha_fin ?? null

  const reintentarReunion = useReintentarReunionGoogle()

  const { form } = useCrearEventoForm({
    onSubmit: async (value: CrearEventoFormValues) => {
      if (!evento) return

      setIsSubmitting(true)
      try {
        const fechaInicio = value.todo_el_dia || !value.hora_inicio
          ? `${value.fecha_inicio}T00:00:00`
          : `${value.fecha_inicio}T${value.hora_inicio}:00`

        let fechaFin: string | null = null
        if (value.fecha_fin) {
          fechaFin = value.todo_el_dia || !value.hora_fin
            ? `${value.fecha_fin}T23:59:59`
            : `${value.fecha_fin}T${value.hora_fin}:00`
        }

        const datos: EventoCalendarioUpdate = {
          titulo: value.titulo,
          descripcion: value.descripcion ?? null,
          tipo: value.tipo as TipoEvento,
          fecha_inicio: fechaInicio,
          fecha_fin: fechaFin,
          todo_el_dia: value.todo_el_dia,
          estado: value.estado as EstadoEvento,
          color: value.color ?? null,
          ubicacion: value.ubicacion ?? null,
          notas: value.notas ?? null,
        }

        await onActualizar(evento.id, datos)
        setEditando(false)
        onOpenChange(false)
      } catch (error) {
        console.error('Error al actualizar evento:', error)
      } finally {
        setIsSubmitting(false)
      }
    },
  })

  // Reset form when evento changes
  useEffect(() => {
    if (evento && open) {
      // Un solo `reset` con los valores del evento: precargar define el punto de partida,
      // no edita. Campo por campo con `setFieldValue` el form nacía "sucio" y la guarda de
      // ORR-392 pedía confirmación apenas se abría el diálogo. `VALORES_INICIALES` va de base
      // para que ningún campo del formulario quede sin definir.
      // `keepDefaultValues` conserva los valores por defecto del formulario: sin eso, `reset`
      // los reemplaza por los del evento y el siguiente re-render los detecta distintos de los
      // que declara el hook, así que vuelve a aplicar los iniciales y el formulario queda vacío.
      form.reset({
        ...VALORES_INICIALES,
        titulo: evento.titulo,
        descripcion: evento.descripcion ?? null,
        tipo: (evento.tipo === 'vencimiento' ? 'otro' : evento.tipo) as CrearEventoFormValues['tipo'],
        fecha_inicio: inicioInfo.fecha,
        hora_inicio: evento.todo_el_dia ? null : inicioInfo.hora,
        fecha_fin: finInfo.fecha || null,
        hora_fin: evento.todo_el_dia ? null : finInfo.hora,
        todo_el_dia: evento.todo_el_dia,
        estado: evento.estado,
        color: evento.color ?? null,
        ubicacion: evento.ubicacion ?? null,
        notas: evento.notas ?? null,
      }, { keepDefaultValues: true })
    }
  }, [evento?.id, open])

  const isDirty = useStore(form.store, (s) => s.isDirty)

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      form.reset()
      setEditando(false)
    }
    onOpenChange(newOpen)
  }

  const descripcionSerie = describirRecurrencia(evento?.regla_recurrencia)

  const handleEliminar = async () => {
    if (!evento) return
    setIsDeleting(true)
    try {
      await onEliminar(evento.id)
      onOpenChange(false)
    } catch (error) {
      console.error('Error al eliminar evento:', error)
    } finally {
      setIsDeleting(false)
    }
  }

  if (!evento) return null

  const tipoConfig = TIPO_EVENTO_CONFIG[evento.tipo] ?? TIPO_EVENTO_CONFIG.otro
  const estadoConfig = ESTADO_EVENTO_CONFIG[evento.estado] ?? ESTADO_EVENTO_CONFIG.pendiente

  // View mode
  if (!editando) {
    return (
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarDays className="h-5 w-5 text-primary" />
              {evento.titulo}
            </DialogTitle>
            <DialogDescription>
              Detalle del evento
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Badges */}
            <div className="flex flex-wrap gap-2">
              <Badge
                variant="outline"
                style={{
                  borderColor: tipoConfig.color,
                  color: tipoConfig.color,
                }}
              >
                {tipoConfig.label}
              </Badge>
              <Badge
                variant="outline"
                style={{
                  borderColor: estadoConfig.color,
                  color: estadoConfig.color,
                }}
              >
                {estadoConfig.label}
              </Badge>
              {evento.color && (
                <span
                  className="w-4 h-4 rounded-full border"
                  style={{ backgroundColor: evento.color }}
                />
              )}
            </div>

            {/* Date & time */}
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Clock className="h-4 w-4" />
                <span>
                  {inicioVisible &&
                    format(parseFechaEvento(inicioVisible), "d 'de' MMMM yyyy", { locale: es })}
                  {!evento.todo_el_dia && inicioVisible && (
                    <> a las {format(parseFechaEvento(inicioVisible), 'HH:mm')}</>
                  )}
                  {finVisible && (
                    <>
                      {' — '}
                      {format(parseFechaEvento(finVisible), "d 'de' MMMM yyyy", { locale: es })}
                      {!evento.todo_el_dia && (
                        <> a las {format(parseFechaEvento(finVisible), 'HH:mm')}</>
                      )}
                    </>
                  )}
                </span>
              </div>

              {evento.ubicacion && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <MapPin className="h-4 w-4" />
                  <span>{evento.ubicacion}</span>
                </div>
              )}
            </div>

            {/* Serie (ORR-383). El cartel sale del RRULE guardado, no de `recurrente`: si la
                fila quedó marcada sin regla que expandir, no hay serie que anunciar. */}
            {descripcionSerie && (
              <div className="flex items-start gap-2 text-muted-foreground text-sm">
                <Repeat className="h-4 w-4 shrink-0 mt-0.5" />
                <span>
                  {descripcionSerie}. Lo que edites acá cambia la serie entera, no solo esta fecha.
                </span>
              </div>
            )}

            {/* Description */}
            {evento.descripcion && (
              <div className="bg-muted/50 p-3 rounded-lg text-sm">
                {evento.descripcion}
              </div>
            )}

            {/* Videollamada (ORR-227 fase 3). Solo en reuniones: el resto de los tipos
                no lleva Meet, así que no hay nada que mostrar ni que reintentar. */}
            {evento.tipo === 'reunion' && (
              <>
                {evento.meet_url ? (
                  <a
                    href={evento.meet_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm text-primary hover:underline"
                  >
                    <Video className="h-4 w-4" />
                    Unirse a la videollamada
                  </a>
                ) : (
                  <div className="flex items-start gap-2 rounded-lg bg-muted/50 p-3 text-sm">
                    <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600 mt-0.5" />
                    <div className="flex-1">
                      <p>La reunión está agendada, pero todavía no tiene link de Meet.</p>
                      {evento.google_sync_error && (
                        <p className="text-xs text-muted-foreground mt-1">
                          {evento.google_sync_error}
                        </p>
                      )}
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => reintentarReunion.mutate(evento)}
                      disabled={reintentarReunion.isPending}
                    >
                      {reintentarReunion.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <RefreshCw className="h-4 w-4" />
                      )}
                      <span className="ml-1.5">Reintentar</span>
                    </Button>
                  </div>
                )}
              </>
            )}

            {/* Invitados con su respuesta en Google. Acá no se editan. */}
            {((evento.participantes?.length ?? 0) > 0 || !!evento.google_event_id) && (
              <InvitadosReunion evento={evento} />
            )}

            {/* Notes */}
            {evento.notas && (
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">Notas</p>
                <p className="text-sm">{evento.notas}</p>
              </div>
            )}
          </div>

          <DialogFooter className="flex-row gap-2 sm:justify-between">
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" size="sm" disabled={isDeleting}>
                  {isDeleting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Trash2 className="h-4 w-4 mr-1" />
                  )}
                  Eliminar
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Eliminar evento</AlertDialogTitle>
                  <AlertDialogDescription>
                    Esta acción no se puede deshacer. Se eliminará el evento &ldquo;{evento.titulo}&rdquo; permanentemente.
                    {descripcionSerie && (
                      <>
                        {' '}
                        <strong>Se cancela la serie entera</strong>, no solo esta fecha, y a los
                        invitados les llega el aviso.
                      </>
                    )}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                  <AlertDialogAction onClick={handleEliminar}>
                    Eliminar
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>

            <Button size="sm" onClick={() => setEditando(true)}>
              <Pencil className="h-4 w-4 mr-1" />
              Editar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )
  }

  // Edit mode
  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <FormDialogContent
        isDirty={isDirty}
        onDiscard={() => handleOpenChange(false)}
        className="sm:max-w-[520px]"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pencil className="h-5 w-5 text-primary" />
            Editar Evento
          </DialogTitle>
          <DialogDescription>
            Modificar datos del evento
          </DialogDescription>
        </DialogHeader>

        <form
          onSubmit={(e) => {
            e.preventDefault()
            form.handleSubmit()
          }}
          className="space-y-4"
        >
          {/* Título */}
          <form.Field name="titulo">
            {(field) => (
              <div className="space-y-2">
                <Label htmlFor={field.name}>Título *</Label>
                <Input
                  id={field.name}
                  value={field.state.value || ''}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                />
                {field.state.meta.errors.length > 0 && (
                  <p className="text-sm text-destructive">
                    {getFieldError(field.state.meta.errors)}
                  </p>
                )}
              </div>
            )}
          </form.Field>

          {/* Tipo + Estado */}
          <div className="grid grid-cols-2 gap-4">
            <form.Field name="tipo">
              {(field) => (
                <div className="space-y-2">
                  <Label>Tipo</Label>
                  <Select
                    value={field.state.value}
                    onValueChange={(v) => field.handleChange(v as CrearEventoFormValues['tipo'])}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TIPOS_SELECCIONABLES.map(([key, config]) => (
                        <SelectItem key={key} value={key}>
                          <div className="flex items-center gap-2">
                            <span
                              className="w-2.5 h-2.5 rounded-full"
                              style={{ backgroundColor: config.color }}
                            />
                            {config.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </form.Field>

            <form.Field name="estado">
              {(field) => (
                <div className="space-y-2">
                  <Label>Estado</Label>
                  <Select
                    value={field.state.value ?? 'pendiente'}
                    onValueChange={(v) => field.handleChange(v as CrearEventoFormValues['estado'])}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(ESTADO_EVENTO_CONFIG).map(([key, config]) => (
                        <SelectItem key={key} value={key}>
                          <div className="flex items-center gap-2">
                            <span
                              className="w-2.5 h-2.5 rounded-full"
                              style={{ backgroundColor: config.color }}
                            />
                            {config.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </form.Field>
          </div>

          {/* Color */}
          <form.Field name="color">
            {(field) => (
              <div className="space-y-2">
                <Label>Color</Label>
                <div className="flex gap-1.5">
                  {COLORES_CALENDARIO.map((color) => (
                    <button
                      key={color}
                      type="button"
                      className={`w-6 h-6 rounded-full border-2 transition-all ${
                        field.state.value === color
                          ? 'border-foreground scale-110'
                          : 'border-transparent hover:scale-105'
                      }`}
                      style={{ backgroundColor: color }}
                      onClick={() =>
                        field.handleChange(
                          field.state.value === color ? null : color
                        )
                      }
                    />
                  ))}
                </div>
              </div>
            )}
          </form.Field>

          {/* Todo el día */}
          <form.Field name="todo_el_dia">
            {(field) => (
              <div className="flex items-center gap-3">
                <Switch
                  id="todo_el_dia_edit"
                  checked={field.state.value}
                  onCheckedChange={field.handleChange}
                />
                <Label htmlFor="todo_el_dia_edit">Todo el día</Label>
              </div>
            )}
          </form.Field>

          {/* Fechas */}
          <form.Subscribe selector={(state) => state.values.todo_el_dia}>
            {(todoElDia) => (
              <div className="grid grid-cols-2 gap-4">
                <form.Field name="fecha_inicio">
                  {(field) => (
                    <div className="space-y-2">
                      <Label htmlFor={field.name}>Fecha inicio *</Label>
                      <Input
                        id={field.name}
                        type="date"
                        value={field.state.value || ''}
                        onChange={(e) => field.handleChange(e.target.value)}
                        onBlur={field.handleBlur}
                      />
                      {field.state.meta.errors.length > 0 && (
                        <p className="text-sm text-destructive">
                          {getFieldError(field.state.meta.errors)}
                        </p>
                      )}
                    </div>
                  )}
                </form.Field>

                {!todoElDia && (
                  <form.Field name="hora_inicio">
                    {(field) => (
                      <div className="space-y-2">
                        <Label htmlFor={field.name}>Hora inicio</Label>
                        <Input
                          id={field.name}
                          type="time"
                          value={field.state.value || ''}
                          onChange={(e) => field.handleChange(e.target.value)}
                        />
                      </div>
                    )}
                  </form.Field>
                )}

                <form.Field name="fecha_fin">
                  {(field) => (
                    <div className="space-y-2">
                      <Label htmlFor={field.name}>Fecha fin</Label>
                      <Input
                        id={field.name}
                        type="date"
                        value={field.state.value || ''}
                        onChange={(e) => field.handleChange(e.target.value)}
                      />
                    </div>
                  )}
                </form.Field>

                {!todoElDia && (
                  <form.Field name="hora_fin">
                    {(field) => (
                      <div className="space-y-2">
                        <Label htmlFor={field.name}>Hora fin</Label>
                        <Input
                          id={field.name}
                          type="time"
                          value={field.state.value || ''}
                          onChange={(e) => field.handleChange(e.target.value)}
                        />
                      </div>
                    )}
                  </form.Field>
                )}
              </div>
            )}
          </form.Subscribe>

          {/* Ubicación */}
          <form.Field name="ubicacion">
            {(field) => (
              <div className="space-y-2">
                <Label htmlFor={field.name}>Ubicación</Label>
                <Input
                  id={field.name}
                  placeholder="Ej: Oficina, Zoom, etc."
                  value={field.state.value || ''}
                  onChange={(e) => field.handleChange(e.target.value)}
                />
              </div>
            )}
          </form.Field>

          {/* Notas */}
          <form.Field name="notas">
            {(field) => (
              <div className="space-y-2">
                <Label htmlFor={field.name}>Notas</Label>
                <Textarea
                  id={field.name}
                  placeholder="Notas adicionales..."
                  className="resize-none"
                  rows={2}
                  value={field.state.value || ''}
                  onChange={(e) => field.handleChange(e.target.value)}
                />
              </div>
            )}
          </form.Field>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setEditando(false)}
              disabled={isSubmitting}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Guardar Cambios
            </Button>
          </DialogFooter>
        </form>
      </FormDialogContent>
    </Dialog>
  )
}
